import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiFetch from '../ApiFetch';
import Header from '../Header';
import Category from '../Category';
import '../../styles/VideoGallery.css';

const API_BASE_URL = `${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1`;
const SCROLL_THRESHOLD = 40;
const TOUCH_THRESHOLD = 60;
const SCROLL_COOLDOWN = 500;
const LIMIT = 20;

const apiClient = {
  get: async (endpoint) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, { credentials: 'include' });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ errors: ['Network error'] }));
      throw new Error(errorData.errors?.join(', ') || `HTTP error! status: ${response.status}`);
    }
    return response.json();
  },
  post: async (endpoint, body) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ errors: ['Network error'] }));
      throw new Error(errorData.errors?.join(', ') || `HTTP error! status: ${response.status}`);
    }
    return response.json();
  },
  delete: async (endpoint) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ errors: ['Network error'] }));
      throw new Error(errorData.errors?.join(', ') || `HTTP error! status: ${response.status}`);
    }
    return response.json();
  },
};

const VideoGallery = () => {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState('');

  const videoRefs = useRef([]);
  const feedRef = useRef(null);
  const lastInteractionRef = useRef(0);
  const touchStartYRef = useRef(null);
  const touchCurrentYRef = useRef(null);

  const dialog = useCallback((title, content) => {
    setDialogContent(content);
    setDialogOpen(true);
  }, []);

  const toggleFavorite = useCallback(async (procId, currentFavorite) => {
    try {
      if (currentFavorite) {
        // いいねを削除
        await apiClient.delete(`/favorite/videogen/${procId}`);
      } else {
        // いいねを追加
        await apiClient.post(`/favorite/videogen/${procId}`, {});
      }

      // ローカルの状態を更新
      setVideos((prevVideos) =>
        prevVideos.map((video) => {
          if (video.proc_id === procId) {
            const newFavorite = currentFavorite ? 0 : 1;
            const newCount = currentFavorite
              ? Math.max(0, (video.favorite_count || 0) - 1)
              : (video.favorite_count || 0) + 1;
            return {
              ...video,
              favorite: newFavorite,
              favorite_count: newCount,
            };
          }
          return video;
        })
      );
    } catch (err) {
      console.error('いいねエラー:', err);
      dialog('エラー', 'いいね操作に失敗しました');
    }
  }, [dialog]);

  const getVideoUrl = useCallback((filePath) => {
    const extractRelativePath = (candidate) => {
      if (!candidate) return null;
      if (typeof candidate === 'string') return candidate;
      if (typeof candidate === 'object') {
        return candidate.path || candidate.url || candidate.file_path || null;
      }
      return null;
    };

    const ensureAbsoluteUrl = (relativePath) => {
      if (!relativePath) return null;
      if (/^https?:\/\//i.test(relativePath)) return relativePath;

      const sanitizedPath = relativePath.replace(/^\/+/, '');
      const protocol = ApiFetch.API_PROTOCOL || (typeof window !== 'undefined' ? window.location.protocol : 'https:');
      const hostFallback = typeof window !== 'undefined' ? window.location.host : '';
      const videoHost = ApiFetch.VIDEO_HOST || ApiFetch.API_HOST || hostFallback;
      const baseUrl = `${protocol}//${videoHost}`;
      const withGeneratedPrefix = sanitizedPath.startsWith('generated/')
        ? sanitizedPath
        : `generated/${sanitizedPath}`;

      return `${baseUrl}/${withGeneratedPrefix}`;
    };

    if (!filePath) return null;

    if (Array.isArray(filePath) && filePath.length > 0) {
      return ensureAbsoluteUrl(extractRelativePath(filePath[0]));
    }

    return ensureAbsoluteUrl(extractRelativePath(filePath));
  }, []);

  // 配列をランダムにシャッフルする関数
  const shuffleArray = useCallback((array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, []);

  const fetchPublicVideos = useCallback(async (pageNum = 1, append = false) => {
    try {
      setLoading(true);
      const offset = (pageNum - 1) * LIMIT;
      const data = await apiClient.get(`/videogen/gallery?limit=${LIMIT}&offset=${offset}`);

      if (data.result) {
        const newVideos = data.data || [];
        const shuffledVideos = shuffleArray(newVideos);
        setVideos((prev) => (append ? [...prev, ...shuffledVideos] : shuffledVideos));
        setTotal(data.total || 0);
        setHasMore(offset + newVideos.length < (data.total || 0));
        if (!append) {
          setCurrentIndex(0);
        }
        setError(null);
      } else {
        setError(data.message || '動画の取得に失敗しました');
      }
    } catch (err) {
      console.error('公開動画取得エラー:', err);
      setError('動画の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [shuffleArray]);

  useEffect(() => {
    fetchPublicVideos(1);
  }, [fetchPublicVideos]);

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchPublicVideos(nextPage, true);
  }, [loading, hasMore, page, fetchPublicVideos]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => {
      if (videos.length === 0) return prev;
      const next = Math.min(prev + 1, videos.length - 1);
      if (next !== prev) {
        lastInteractionRef.current = Date.now();
      }
      return next;
    });
  }, [videos.length]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => {
      const next = Math.max(prev - 1, 0);
      if (next !== prev) {
        lastInteractionRef.current = Date.now();
      }
      return next;
    });
  }, []);

  const handleWheel = useCallback((event) => {
    if (videos.length === 0) return;
    if (event?.preventDefault) {
      event.preventDefault();
    }
    const now = Date.now();
    if (now - lastInteractionRef.current < SCROLL_COOLDOWN) return;

    if (event.deltaY > SCROLL_THRESHOLD) {
      goToNext();
    } else if (event.deltaY < -SCROLL_THRESHOLD) {
      goToPrev();
    }
  }, [videos.length, goToNext, goToPrev]);

  const handleTouchStart = useCallback((event) => {
    if (videos.length === 0) return;
    const touchY = event.touches[0].clientY;
    touchStartYRef.current = touchY;
    touchCurrentYRef.current = touchY;
  }, [videos.length]);

  const handleTouchMove = useCallback((event) => {
    if (touchStartYRef.current === null) return;
    event.preventDefault();
    touchCurrentYRef.current = event.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (touchStartYRef.current === null || touchCurrentYRef.current === null) {
      touchStartYRef.current = null;
      touchCurrentYRef.current = null;
      return;
    }

    const delta = touchStartYRef.current - touchCurrentYRef.current;
    touchStartYRef.current = null;
    touchCurrentYRef.current = null;

    if (Math.abs(delta) < TOUCH_THRESHOLD) return;

    if (delta > 0) {
      goToNext();
    } else {
      goToPrev();
    }
  }, [goToNext, goToPrev]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      goToNext();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      goToPrev();
    }
  }, [goToNext, goToPrev]);

  const toggleVideoPlayback = useCallback((index) => {
    const target = videoRefs.current[index];
    if (!target) return;
    if (target.paused) {
      const playPromise = target.play();
      if (playPromise) {
        playPromise.catch(() => {});
      }
    } else {
      target.pause();
    }
  }, []);

  useEffect(() => {
    videoRefs.current = videoRefs.current.slice(0, videos.length);
  }, [videos]);

  useEffect(() => {
    setCurrentIndex((prev) => {
      if (videos.length === 0) {
        return 0;
      }
      return Math.min(prev, videos.length - 1);
    });
  }, [videos.length]);

  useEffect(() => {
    const cleanupFunctions = [];
    
    videoRefs.current.forEach((videoEl, idx) => {
      if (!videoEl) return;
      
      if (idx === currentIndex) {
        const playPromise = videoEl.play();
        if (playPromise) {
          playPromise.catch(() => {});
        }
        
        // 動画終了時に次の動画へ自動遷移
        const handleEnded = () => {
          if (idx < videos.length - 1) {
            goToNext();
          }
        };
        
        videoEl.addEventListener('ended', handleEnded);
        cleanupFunctions.push(() => {
          videoEl.removeEventListener('ended', handleEnded);
        });
      } else {
        videoEl.pause();
        videoEl.currentTime = 0;
      }
    });
    
    // クリーンアップ
    return () => {
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  }, [currentIndex, videos, goToNext]);

  useEffect(() => {
    if (!loading && hasMore && videos.length > 0 && currentIndex >= videos.length - 3) {
      loadMore();
    }
  }, [currentIndex, videos.length, hasMore, loading, loadMore]);

  useEffect(() => {
    if (videos.length > 0) {
      feedRef.current?.focus();
    }
  }, [videos.length]);

  // ホイールイベントとタッチイベントのパッシブを無効化
  useEffect(() => {
    const feedElement = feedRef.current;
    if (!feedElement) return;

    const wheelHandler = (e) => handleWheel(e);
    const touchMoveHandler = (e) => handleTouchMove(e);

    // パッシブを明示的にfalseに設定
    feedElement.addEventListener('wheel', wheelHandler, { passive: false });
    feedElement.addEventListener('touchmove', touchMoveHandler, { passive: false });

    return () => {
      feedElement.removeEventListener('wheel', wheelHandler);
      feedElement.removeEventListener('touchmove', touchMoveHandler);
    };
  }, [handleWheel, handleTouchMove, videos.length]);

  return (
    <div className="wrapper video-gallery-page">
      <header className="App-header"></header>
      <Header />
      <Category />

      <section className="video-gallery-section">
        <div className="video-gallery-container">
          <div className="video-gallery-header">
            <div className="video-gallery-heading">
              <h1 className="video-gallery-title">
                <i className="fas fa-film"></i>
                <span className="video-gallery-title-text">公開動画ギャラリー</span>
              </h1>
              <p className="video-gallery-subtitle video-gallery-subtitle-text">
                コミュニティで共有された動画を閲覧できます
              </p>
            </div>
            <button
              className="btn-create-video"
              onClick={() => navigate('/generate/video')}
            >
              <i className="fas fa-plus"></i>
              動画を生成する
            </button>
          </div>

          {error ? (
            <div className="video-gallery-error">
              <i className="fas fa-exclamation-triangle"></i>
              <p>{error}</p>
            </div>
          ) : (
            <>
              <div
                className={`video-gallery-feed${videos.length === 0 ? ' is-empty' : ''}`}
                ref={feedRef}
                tabIndex={0}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                onKeyDown={handleKeyDown}
              >
                {videos.length > 0 ? (
                  <div
                    className="video-feed-track"
                    style={{ transform: `translateY(-${currentIndex * 100}%)` }}
                  >
                    {videos.map((video, index) => {
                      const videoUrl = getVideoUrl(video.file_path);
                      const key = video.proc_id ?? `${index}-${videoUrl ?? 'video'}`;

                      return (
                        <div
                          key={key}
                          className={`video-feed-item${index === currentIndex ? ' active' : ''}`}
                          style={{ top: `${index * 100}%` }}
                        >
                          {videoUrl ? (
                            <video
                              ref={(el) => {
                                videoRefs.current[index] = el;
                              }}
                              className="video-feed-video"
                              src={videoUrl}
                              playsInline
                              preload="metadata"
                              onClick={() => toggleVideoPlayback(index)}
                            />
                          ) : (
                            <div className="video-feed-placeholder">
                              <i className="fas fa-video-slash"></i>
                              <p>動画が利用できません</p>
                            </div>
                          )}

                          <div className="video-feed-overlay">
                            <div className="video-feed-author">
                              <i className="fas fa-user"></i>
                              <span>{video.display_name || video.user_name || '匿名'}</span>
                            </div>
                            <p className="video-feed-prompt">{video.prompt}</p>
                            <div className="video-feed-meta-row">
                              {video.style && (
                                <span className="video-feed-chip">
                                  <i className="fas fa-palette"></i>
                                  {video.style}
                                </span>
                              )}
                              {video.aspect_ratio && (
                                <span className="video-feed-chip">
                                  <i className="fas fa-expand"></i>
                                  {video.aspect_ratio}
                                </span>
                              )}
                              {video.duration_seconds && (
                                <span className="video-feed-chip">
                                  <i className="fas fa-clock"></i>
                                  {video.duration_seconds}秒
                                </span>
                              )}
                              <span className="video-feed-chip">
                                <i className="fas fa-calendar"></i>
                                {new Date(video.create_datetime).toLocaleDateString('ja-JP')}
                              </span>
                            </div>
                          </div>

                          <div className="video-feed-actions">
                            <button
                              className="video-feed-favorite-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFavorite(video.proc_id, video.favorite === 1);
                              }}
                            >
                              <span className="heart-icon">{video.favorite === 1 ? '❤️' : '🤍'}</span>
                              <span className="favorite-count">{video.favorite_count || 0}</span>
                            </button>
                          </div>

                          <div className="video-feed-progress">
                            <span>{index + 1} / {total || videos.length}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  !loading && (
                    <div className="video-gallery-empty feed-empty">
                      <i className="fas fa-video-slash"></i>
                      <p>公開されている動画はまだありません</p>
                    </div>
                  )
                )}

                {loading && (
                  <div className={`video-gallery-loading ${videos.length > 0 ? 'feed-loading' : ''}`}>
                    <div className="spinner"></div>
                    <p>読み込み中...</p>
                  </div>
                )}
              </div>

              {!loading && hasMore && videos.length > 0 && (
                <div className="video-gallery-load-more">
                  <button onClick={loadMore} className="btn-load-more">
                    もっと見る ({videos.length} / {total || videos.length})
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default VideoGallery;
